// Unsent comment text, saved in IndexedDB so a reload does not lose it.
//
// Each box saves what it shows under its own key. When a write sends that
// text (see Writes), it takes it out of the box into the box's outbox
// (`takeDraft`): the box is free for new text, and the write keeps its own
// copy. The outbox is deleted once the write succeeds (`sentDraft`); when
// the write is given up, its text goes back to the box, merged with
// whatever was typed meanwhile (`returnDraft`). An outbox left by a page
// that closed with the write unsent comes back when its box loads.
//
// Every operation on a box runs after the previous one on that box has
// finished (one queue per key), and each read-modify-write is one
// IndexedDB transaction, so operations started back to back never
// interleave.
import { idbGet, idbUpdate } from '../api/db';

const outbox = (key: string) => `outbox:${key}`;

type Record_ = { key: string; body: string; at: number };
const record = (key: string, body: string): Record_ | undefined => (body.trim() ? { key, body, at: Date.now() } : undefined);

/** The operations on each box, chained in the order they were started. */
const queues = new Map<string, Promise<unknown>>();

function queued<T>(key: string, op: () => Promise<T>): Promise<T> {
  const run = (queues.get(key) ?? Promise.resolve()).then(op, op);
  const tail = run.catch(() => {});
  queues.set(key, tail);
  void tail.then(() => {
    if (queues.get(key) === tail) queues.delete(key);
  });
  return run;
}

/** Update box `key` and its outbox in one transaction. */
function update(key: string, next: (box: string, out: string) => [string, string]): Promise<void> {
  return queued(key, async () => {
    await idbUpdate('drafts', [key, outbox(key)], ([b, o]) => {
      const [box, out] = next(b?.body ?? '', o?.body ?? '');
      return [box === (b?.body ?? '') ? b : record(key, box), out === (o?.body ?? '') ? o : record(outbox(key), out)];
    });
  });
}

/**
 * The saved text of box `key`. With `outbox`, also the text a write took
 * from it and never sent (a closed page's), merged in and saved back; ask
 * for it only when no write or Retry holds the box (`Writes.holds`).
 */
export async function loadDraft(key: string, opts: { outbox?: boolean } = {}): Promise<string> {
  if (opts.outbox) await update(key, (box, out) => [mergeText(box, out), '']);
  return queued(key, async () => (await idbGet('drafts', key))?.body ?? '');
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();

export function saveDraft(key: string, body: string) {
  clearTimeout(timers.get(key));
  timers.set(
    key,
    setTimeout(() => {
      timers.delete(key);
      void update(key, (_, out) => [body, out]);
    }, 250),
  );
}

export function clearDraft(key: string) {
  clearTimeout(timers.get(key));
  timers.delete(key);
  void update(key, (_, out) => ['', out]);
}

/**
 * A write takes `text` out of box `key` to send it: kept in the outbox, and
 * gone from the box (what else the box holds stays).
 */
export function takeDraft(key: string, text: string): Promise<void> {
  clearTimeout(timers.get(key));
  timers.delete(key);
  return update(key, (box) => [withoutText(box, text), text]);
}

/** The write that took box `key`'s text succeeded: its copy goes. Text typed since stays. */
export function sentDraft(key: string): Promise<void> {
  return update(key, (box) => [box, '']);
}

/** The write that took box `key`'s text was given up: the text goes back, merged with what was typed since. */
export function returnDraft(key: string, text: string): Promise<void> {
  return update(key, (box) => [mergeText(box, text), '']);
}

/** Two unsent texts as one, neither lost: either alone when the other is empty or already in it, else both, the first first. */
export function mergeText(a: string, b: string): string {
  if (!b.trim() || a.includes(b.trim())) return a;
  if (!a.trim() || b.includes(a.trim())) return b;
  return `${a.replace(/\s+$/, '')}\n\n${b}`;
}

/** `box` without `text` taken out of it: empty when the box held only it (or an earlier save of it). */
function withoutText(box: string, text: string): string {
  const t = text.trim();
  if (!box.trim() || t.includes(box.trim())) return '';
  const at = box.indexOf(t);
  if (at < 0) return box;
  return (box.slice(0, at) + box.slice(at + t.length)).replace(/^\s+|\s+$/g, '').replace(/\n{3,}/g, '\n\n');
}
